import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Avatar
} from '@mui/material';
import {
  Search,
  TrendingUp,
  Lightbulb,
  CheckCircle,
  Star,
  Business,
  LocationOn
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import axios from 'axios';

const SuggestionPage = () => {
  const { locationId } = useParams();
  const { selectedBusiness } = useBusiness();
  
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  // Reset page when selected business changes
  useEffect(() => {
    console.log('🔄 Selected business changed, resetting Ranking Vorschläge page');
    setKeyword('');
    setAnalysis(null);
    setError('');
    setLoading(false);
  }, [selectedBusiness]);

  const analyzeCompetitors = async () => {
    if (!keyword.trim()) {
      setError('Bitte geben Sie ein Keyword ein');
      return;
    }

    if (!selectedBusiness) {
      setError('Unternehmen nicht geladen. Bitte wählen Sie ein Unternehmen aus.');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis(null);

    try {
      // Extract city from address
      let city = '';
      if (selectedBusiness.address) {
        if (typeof selectedBusiness.address === 'string') {
          city = selectedBusiness.address;
        } else if (selectedBusiness.address.city) {
          city = selectedBusiness.address.city;
        } else if (selectedBusiness.address.formattedAddress) {
          city = selectedBusiness.address.formattedAddress;
        }
      }

      const response = await axios.post(`/api/businesses/${locationId}/competitor-suggestions`, {
        keyword: keyword.trim(),
        city: city
      });

      if (response.data.success) {
        setAnalysis(response.data.data);
      }
    } catch (err) {
      console.error('Error analyzing competitors:', err);
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rank) => {
    if (rank <= 3) return amDesignColors.success; // Green
    if (rank <= 5) return amDesignColors.warning; // Orange
    return amDesignColors.error; // Red
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Ranking-Optimierung Vorschläge
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Analysieren Sie Top-Konkurrenten und erhalten Sie präzise Empfehlungen für bessere Rankings.
        </Typography>
      </Box>

      {/* Search Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Keyword analysieren"
                placeholder="z.B. zahnarzt, restaurant münchen, elektriker"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                onKeyPress={(e) => e.key === 'Enter' && analyzeCompetitors()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={analyzeCompetitors}
                disabled={loading || !keyword.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <TrendingUp />}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Analysiere...' : 'Konkurrenten analysieren'}
              </Button>
            </Grid>
          </Grid>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysis && (
        <Grid container spacing={3}>
          {/* Top Competitors */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ mr: 1 }} />
                  Top 5 Konkurrenten
                </Typography>
                
                <List sx={{ p: 0 }}>
                  {analysis.topCompetitors?.map((competitor, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              bgcolor: getRankColor(competitor.rank),
                              color: amDesignColors.white,
                              fontWeight: 'bold',
                              width: 32,
                              height: 32
                            }}
                          >
                            {competitor.rank}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography variant="subtitle1" fontWeight={500}>
                                {competitor.name}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                {competitor.rating && (
                                  <Chip
                                    label={`${competitor.rating} ⭐`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {competitor.reviewCount && (
                                  <Chip
                                    label={`${competitor.reviewCount} Bewertungen`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              <LocationOn sx={{ fontSize: '1rem', verticalAlign: 'text-bottom', mr: 0.5 }} />
                              {competitor.address}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < analysis.topCompetitors.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* AI Insights */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Lightbulb sx={{ mr: 1, color: 'warning.main' }} />
                  Ranking-Faktoren
                </Typography>
                
                {/* Why they rank better */}
                {analysis.rankingFactors && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.light' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'info.dark' }}>
                      🏆 Warum sie besser ranken:
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {analysis.rankingFactors.map((factor, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckCircle sx={{ fontSize: '1rem', color: 'info.main' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2">
                                {factor}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Improvement suggestions */}
                {analysis.suggestions && (
                  <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'success.dark' }}>
                      🚀 Verbesserungsvorschläge:
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {analysis.suggestions.map((suggestion, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <TrendingUp sx={{ fontSize: '1rem', color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2">
                                {suggestion}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Analysis Summary */}
      {analysis?.summary && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Star sx={{ mr: 1, color: 'primary.main' }} />
              Zusammenfassung
            </Typography>
            
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {analysis.summary}
            </Typography>
            
            {analysis.metadata && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid amDesignColors.border' }}>
                <Typography variant="caption" color="textSecondary">
                  Analysiert: {new Date(analysis.metadata.analyzedAt).toLocaleString('de-DE')} • 
                  Keyword: "{analysis.metadata.keyword}" • 
                  Konkurrenten: {analysis.metadata.competitorCount}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !analysis && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Lightbulb sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Ranking-Analyse starten
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400, mx: 'auto' }}>
              Geben Sie ein Keyword ein, um Top-Konkurrenten zu analysieren und 
              konkrete Verbesserungsvorschläge für Ihr Ranking zu erhalten.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SuggestionPage;